import { useAuth0 } from "ct-auth"
import { Backdrop, Button, Stack } from "@mui/material"
import { Fragment, useCallback, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { useQueries } from "../../data-utils/reactQueryHelpers.js"
import { userOrg } from "../../data-utils/authUtils.js"
import { useOrgBranding } from "../../hooks/useOrgBranding.js"
import logger from "../../Logger/index.js"
import { AuthActions } from "../../store/auth-slice"
import { UiActions } from "../../store/ui-slice"
import { DEFAULT_LOGO_BLACK, HEADER_NAV_ICONS } from "../../utilities/constants"
import "./Header.scss"

const pageLogger = logger.child({ module: "Header" })

/**
 * Header (CT Modern shell): the app no longer has a top chrome bar — brand +
 * theme toggle live at the top of the sidebar rail and the account menu lives in
 * the rail footer (see SidePanel/UserMenu), matching the company's other product.
 *
 * This component now renders ONLY the print-preview header (reports need the
 * logo + printing controls), and keeps its two state-sync effects (auth login
 * state + game-view nav flags) that other screens rely on.
 */
function Header({ currentLocation }) {
  const { user } = useAuth0()

  const { data: orgData } = useQueries.HOME_USER_ORG({ orgId: userOrg(user) })
  const { logoUrl, isCustomLogo } = useOrgBranding(orgData)
  // This header only renders in the printable (white-page) report view. The
  // default ChaosTrack mark (logo_placeholder.png) is orange-only and is barely
  // visible on white, so fall back to the dark wordmark variant when no custom
  // org logo is set. Custom org/partner logos are used as-is.
  const reportLogoUrl = isCustomLogo ? logoUrl : DEFAULT_LOGO_BLACK

  const dispatch = useDispatch()
  const currentPath = useLocation()
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn)
  const printable = useSelector((state) => state.ui.showPrintable)

  // Cancel 'printable' (preview) state — restore the user's preferred theme
  // (print preview forces light; the old code restored a hard-coded dark).
  const cancelPrintable = () => {
    dispatch(UiActions.restoreUserTheme())
    dispatch(UiActions.setShowPrintable(false))
  }

  const updateNavIconViewStates = useCallback(
    (iconName) => {
      dispatch(UiActions.setShowGameView(iconName === HEADER_NAV_ICONS.GAME_VIEW))
    },
    [dispatch]
  )

  const updateNavIconActiveStates = useCallback(
    (currentPathName, iconName) => {
      dispatch(
        UiActions.setGameViewActive(
          iconName === HEADER_NAV_ICONS.GAME_VIEW && currentPathName.includes("/games") && !currentPathName.includes("/games/")
        )
      )
    },
    [dispatch]
  )

  useEffect(() => {
    const updateUserData = (theUser) => {
      // TODO: Revisit this
      if (theUser) {
        dispatch(AuthActions.login())
      } else {
        dispatch(AuthActions.logout())
      }
    }

    pageLogger.info({ user }, `Current location: '${currentLocation}' with user`)
    updateUserData(user)
  }, [currentLocation, user, dispatch])

  useEffect(() => {
    // Navigation Icons
    for (const navIcons in HEADER_NAV_ICONS) {
      updateNavIconViewStates(navIcons)
      updateNavIconActiveStates(currentPath.pathname, navIcons)
    }
  }, [currentPath, updateNavIconActiveStates, updateNavIconViewStates])

  const handleLogoClick = (e) => {
    e.preventDefault()
  }

  return isLoggedIn && printable ? (
    <Fragment>
      <header className="d-flex flex-wrap justify-content-between p-3 border-bottom">
        <a href="/" onClick={handleLogoClick} className="d-flex align-items-center mb-2 text-decoration-none">
          <img
            className="brand-logo"
            src={reportLogoUrl}
            onError={(e) => {
              if (e.currentTarget.dataset.fellBack) return
              e.currentTarget.dataset.fellBack = "1"
              e.currentTarget.src = DEFAULT_LOGO_BLACK
            }}
            alt="brand logo"
          />
        </a>

        <div className="page-header-right d-flex align-items-center">
          <span className="avatar-wrapper me-2">
            <img src={user?.picture ?? "images/people-male.png"} alt="profile-icon" className="rounded-circle profile-image" width="32" height="32" />
          </span>
          <span className="profile-name">{user?.email ?? ""}</span>
        </div>
      </header>
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1, bottom: "unset" }} open={true} invisible={true}>
        <Stack direction="row" justifyContent="center" alignItems="flex-start" sx={{ width: "100%", height: "100%" }}>
          <Button variant="solid" color="primary" className="print-btn no-print" onClick={() => window.print()}>
            Print
          </Button>
          <Button variant="solid" color="primary" className="print-btn no-print" onClick={cancelPrintable}>
            Cancel
          </Button>
        </Stack>
      </Backdrop>
    </Fragment>
  ) : null
}

export default Header
